// Phase 1a Polish — Create a new asset.
//
// Three branches, gated by top-of-page tabs (Dynamic / Stable / Kit):
//   - Auto-named dynamic (SmartCharger, TimeMachine): preview the auto-computed name
//     and VPN from the server, operator sees what will be created before submitting.
//     Extra fields (power, firmware, notes) optional at create.
//   - Named dynamic (RTK Unit, future custom types): operator types the asset name.
//   - Stable (tarp, generator, pelican, etc.): server auto-issues prefix + sequence.
//   - Kit (ground_station_kit): operator names it; children attached later on AssetDetail.

import React, { useState, useEffect, useMemo } from 'react';
import { buttonClass } from '../components/ui/Button';
import { useHistory, useLocation } from 'react-router-dom';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import useAssetsStore from '../stores/useAssetsStore';
import { AssetType, Asset, AssetCategory, AssetNamePreview } from './types';
import { EXTENSION_FIELD_SCHEMAS, ExtensionFieldDef } from './extension-field-schemas';
import PageLayout from '../components/ui/PageLayout';
import Reveal from '../components/ui/Reveal';
import Checkbox from '../components/ui/Checkbox';
import { Field, FieldRow, TextArea } from '../components/ui/Field';
import SegTrack from '../components/ui/SegTrack';
import { Panel } from '../components/ui/Panel';
import Select from '../components/ui/Select';

const AUTO_NAMED_KEYS = new Set(['smart_charger', 'time_machine']);

const TAB_LABEL: Record<AssetCategory, string> = {
  dynamic: 'Dynamic',
  stable:  'Stable',
  kit:     'Kit',
};

const NewAssetForm: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const api = useApi();
  const toast = useToast();
  const { types, refreshTypes } = useAssetsStore();

  // Initial category from ?category= query param so callers like AssetsStableList can
  // deep-link the operator straight into the right tab. Falls back to 'dynamic'.
  const initialTab = useMemo<AssetCategory>(() => {
    const params = new URLSearchParams(location.search);
    const c = params.get('category');
    return c === 'stable' || c === 'kit' || c === 'dynamic' ? c : 'dynamic';
    // Read once on mount; deep-link is a one-shot hint, not a live binding.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [tab, setTab]     = useState<AssetCategory>(initialTab);
  const [typeId, setTypeId] = useState<number | null>(null);
  const [assetName, setAssetName] = useState('');
  const [notes, setNotes] = useState('');
  const [extension, setExtension] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<AssetNamePreview | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // refreshTypes (not ensureTypesLoaded) every time we land on the form so cached
  // sequence_next values for auto-issue types stay current. Without this, returning
  // to /assets/new after a create would show the previous next-number until the
  // browser tab is refreshed (e.g. "TARP0063" lingering after creating TARP0063).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refreshTypes().catch(() => toast.error('Failed to load asset types')); }, []);

  // Once types are loaded, honor a ?type=<key> query param by selecting that type.
  // Runs once when types first arrive — won't re-fire if the user manually changes
  // the type picker. Drops the typeId if the deep-linked type is on a different tab
  // than initialTab (rare; happens only with a malformed link).
  useEffect(() => {
    if (typeId !== null || types.length === 0) return;
    const params = new URLSearchParams(location.search);
    const typeKey = params.get('type');
    if (!typeKey) return;
    const matched = types.find(t => t.key === typeKey && t.is_active && t.category === initialTab);
    if (matched) setTypeId(matched.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [types]);

  const selectedType: AssetType | undefined = useMemo(
    () => types.find(t => t.id === typeId),
    [types, typeId]
  );

  // Reset all fields when switching types so state from a prior selection doesn't leak.
  // Checkboxes get pre-seeded from defaultChecked so the form state matches what the
  // user visually sees (otherwise an unchanged "checked" box would never POST a value).
  useEffect(() => {
    setAssetName('');
    const seedExt: Record<string, string> = {};
    if (selectedType) {
      for (const f of EXTENSION_FIELD_SCHEMAS[selectedType.key] ?? []) {
        if (f.inputType === 'checkbox') seedExt[f.key] = f.defaultChecked ? 'true' : 'false';
      }
    }
    setExtension(seedExt);
    setPreview(null);
    if (selectedType && AUTO_NAMED_KEYS.has(selectedType.key)) {
      // Fetch the next auto-computed name + VPN. Non-reserving — just a preview.
      api.get(`/assets/preview-name?type_key=${selectedType.key}`)
        .then((p: AssetNamePreview) => setPreview(p))
        .catch(() => setPreview(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId]);

  // Switching tabs clears the type selection so we don't render a stale type for a different category.
  useEffect(() => {
    setTypeId(null);
  }, [tab]);

  const filteredTypes = useMemo(
    () => types.filter(t => t.is_active && t.category === tab),
    [types, tab]
  );

  const isAutoNamed = !!selectedType && AUTO_NAMED_KEYS.has(selectedType.key);
  const isAutoIssue = !!selectedType?.auto_issue_display_code;
  const needsAssetName = !!selectedType && !isAutoNamed && !isAutoIssue;

  const extensionFields: ExtensionFieldDef[] = selectedType
    ? (EXTENSION_FIELD_SCHEMAS[selectedType.key] ?? [])
    : [];

  const handleSubmit = async () => {
    if (!selectedType) { toast.error('Select an asset type'); return; }
    if (needsAssetName && !assetName.trim()) {
      toast.error('Asset Name is required');
      return;
    }

    const body: Record<string, unknown> = { asset_type_id: selectedType.id };
    if (needsAssetName) body.display_code = assetName.trim();
    if (notes.trim())   body.notes = notes.trim();

    if (extensionFields.length > 0) {
      const extPayload: Record<string, unknown> = {};
      for (const f of extensionFields) {
        const v = extension[f.key];
        if (v === undefined || v === '') continue;
        // Checkbox fields carry 'true'/'false' strings in form state; coerce to real
        // booleans so the backend Knex INSERT writes the column without relying on
        // Postgres' string→boolean cast.
        extPayload[f.key] = f.inputType === 'checkbox' ? v === 'true' : v;
      }
      if (Object.keys(extPayload).length > 0) body.extension = extPayload;
    }

    setSubmitting(true);
    try {
      const created: Asset = await api.post('/assets', { body });
      toast.success(`Created ${created.display_code}`);
      history.push(`/assets/${created.id}`);
    } catch (err) {
      const e = err as { status?: number; message?: string };
      toast.error(e.message || 'Failed to create asset');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageLayout kind="focus" className="pt-4">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl font-bold text-primary">New Asset</h1>
        <button onClick={() => history.goBack()} className={buttonClass('tonal', 'md')}>Cancel</button>
      </div>

      {/* Category filter: scopes the type picker below. */}
      <SegTrack
        label="Asset category"
        className="mb-4"
        value={tab}
        onChange={key => setTab(key as AssetCategory)}
        options={(['dynamic', 'stable', 'kit'] as AssetCategory[]).map(c => ({ key: c, label: TAB_LABEL[c] }))}
      />

      <Panel
        label="New asset"
        className="mb-3"
        footer={
          <div className="flex justify-end gap-2">
            <button onClick={() => history.goBack()} className={buttonClass('quiet', 'md')}>Cancel</button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !selectedType}
              className={buttonClass('filled', 'md')}
            >
              {submitting ? 'Creating…' : 'Create Asset'}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <FieldRow label="Asset Type">
              <Select
                label="Asset type"
                value={typeId === null ? '' : String(typeId)}
                onChange={key => setTypeId(key ? parseInt(key, 10) : null)}
                options={[
                  { key: '', label: `Select a ${TAB_LABEL[tab].toLowerCase()} type…` },
                  ...filteredTypes.map(t => ({
                    key: String(t.id),
                    label: `${t.label}${t.auto_issue_display_code ? ` (auto-${t.barcode_prefix})` : ''}`,
                  })),
                ]}
              />
            </FieldRow>

            <Reveal show={!!(selectedType && isAutoNamed)}>
              <div className="text-[11px] text-on-surface-variant mt-1.5">
                {preview
                  ? <>Will be named <span className="text-amber-300 font-mono">{preview.display_code}</span>
                      {preview.vpn_ip && <> · VPN <span className="text-amber-300 font-mono">{preview.vpn_ip}</span></>}
                      {' · starts as Building until Build Phase is set to In Service'}
                    </>
                  : 'Computing next available name…'}
              </div>
            </Reveal>
            <Reveal show={!!(selectedType && isAutoIssue)}>
              <div className="text-[11px] text-on-surface-variant mt-1.5">
                Auto-named as{' '}
                <span className="text-amber-300 font-mono">
                  {selectedType?.barcode_prefix}{String(selectedType?.sequence_next ?? 0).padStart(4, '0')}
                </span>
              </div>
            </Reveal>
            <Reveal show={!!(selectedType && needsAssetName)}>
              <div className="text-[11px] text-on-surface-variant mt-1.5">
                You'll name this asset below.{selectedType?.is_built_in_house && ' Starts as WIP inventory.'}
              </div>
            </Reveal>
          </div>

          <Reveal show={!!needsAssetName}>
            <FieldRow label="Asset Name">
              <Field
                type="text"
                value={assetName}
                onChange={e => setAssetName(e.target.value)}
                placeholder={`${selectedType?.label.replace(/\s+/g, '')}00AF`}
                className="font-mono"
              />
            </FieldRow>
          </Reveal>

          <Reveal show={extensionFields.length > 0}>
            <div className="pt-3 border-t border-gray-700/40">
              <div className="text-[11px] text-on-surface-variant uppercase tracking-widest font-semibold mb-3">
                {selectedType?.label} Details
              </div>
              <div className="space-y-3">
                {extensionFields.map(f => {
                  if (f.inputType === 'checkbox') {
                    return (
                      <Checkbox
                        key={f.key}
                        fill
                        checked={extension[f.key] === 'true'}
                        onChange={e => setExtension(prev => ({ ...prev, [f.key]: e.target.checked ? 'true' : 'false' }))}
                        label={f.label}
                        description={f.helpText}
                      />
                    );
                  }
                  return (
                    <FieldRow key={f.key} label={f.label} hint={f.helpText}>
                      {f.inputType === 'select' ? (
                        <Select
                          label={f.label}
                          value={extension[f.key] ?? ''}
                          onChange={key => setExtension(prev => ({ ...prev, [f.key]: key }))}
                          options={[{ key: '', label: 'None' }, ...(f.options ?? []).map(o => ({ key: o.value, label: o.label }))]}
                        />
                      ) : (
                        <Field
                          type={f.inputType ?? 'text'}
                          value={extension[f.key] ?? ''}
                          onChange={e => setExtension(prev => ({ ...prev, [f.key]: e.target.value }))}
                          placeholder={f.placeholder}
                          className={f.mono ? 'font-mono' : ''}
                        />
                      )}
                    </FieldRow>
                  );
                })}
              </div>
            </div>
          </Reveal>

          <div className="pt-3 border-t border-gray-700/40">
            <FieldRow label="Notes (optional)">
              <TextArea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={3}
                className="!resize-none"
              />
            </FieldRow>
          </div>
        </div>
      </Panel>
    </PageLayout>
  );
};

export default NewAssetForm;
